import ApartmentIcon from '@mui/icons-material/Apartment';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Autocomplete, Box, CircularProgress, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { getUser } from 'src/utils/Common';
import ToaScheduling from './ToaScheduling';
import axios, { getSelectedServiceShop, setSelectedServiceShop } from './api';

const REGION_STORAGE_KEY = 'aronRotam:selectedRegion';
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 300000, gcTime: 600000, refetchOnWindowFocus: false, retry: 1 } }
});

const readStoredRegion = () => {
  try { return JSON.parse(sessionStorage.getItem(REGION_STORAGE_KEY)); } catch { return null; }
};
const optionLabel = option => option?.Dsc || option?.name || '';
const optionEquals = (option, value) => String(option?.BirimKodu) === String(value?.BirimKodu);

function SelectionField({ label, placeholder, value, options, loading, disabled, icon, onChange }) {
  return (
    <Autocomplete
      fullWidth size="small" value={value || null} options={options} loading={loading} disabled={disabled}
      getOptionLabel={optionLabel} isOptionEqualToValue={optionEquals}
      onChange={(event, newValue) => onChange(newValue)} noOptionsText="Sonuç bulunamadı" loadingText="Yükleniyor..."
      renderInput={params => (
        <TextField {...params} label={label} placeholder={placeholder} InputProps={{
          ...params.InputProps,
          startAdornment: <InputAdornment position="start" sx={{ color: 'primary.main', ml: 0.25 }}>{icon}</InputAdornment>,
          endAdornment: <>{loading ? <CircularProgress color="primary" size={18} /> : null}{params.InputProps.endAdornment}</>
        }} />
      )}
      slotProps={{ paper: { elevation: 8, sx: { mt: 0.75, borderRadius: 2, border: '1px solid', borderColor: 'divider' } } }}
    />
  );
}

SelectionField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.object,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  icon: PropTypes.node.isRequired,
  onChange: PropTypes.func.isRequired
};

function AronRotamContent() {
  const currentUser = getUser() || {};
  const storedService = getSelectedServiceShop();
  const [regions, setRegions] = useState([]);
  const [services, setServices] = useState([]);
  const [region, setRegion] = useState(() => readStoredRegion());
  const [serviceShop, setServiceShop] = useState(storedService);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectionError, setSelectionError] = useState('');

  const selectService = (item, selectedRegion) => {
    const normalized = item ? {
      ...item, id: item.BirimKodu, name: item.Dsc, regionId: selectedRegion?.BirimKodu
    } : null;
    setServiceShop(normalized);
    setSelectedServiceShop(normalized);
  };

  const loadServices = async (selectedRegion, preferredService = null) => {
    if (!selectedRegion?.BirimKodu) { setServices([]); return; }
    setLoadingServices(true);
    setSelectionError('');
    try {
      const regionCode = [4, 6].includes(Number(currentUser.ManagementRoleId))
        ? currentUser.ServiceShopId : selectedRegion.BirimKodu;
      const response = await axios.get(`/GetBirimData/${regionCode}`);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setServices(rows);
      const matched = preferredService
        ? rows.find(item => String(item.BirimKodu) === String(preferredService.id)) : null;
      if (matched) selectService(matched, selectedRegion);
      else if (rows.length === 1) selectService(rows[0], selectedRegion);
      else if (preferredService) selectService(null, selectedRegion);
    } catch {
      setServices([]);
      setSelectionError('Servis listesi alınamadı. Lütfen tekrar deneyin.');
    } finally { setLoadingServices(false); }
  };

  useEffect(() => {
    let active = true;
    const loadRegions = async () => {
      setLoadingRegions(true);
      setSelectionError('');
      try {
        const response = await axios.get(`/GetTopServiceCode/${currentUser.TopServiceShopCode}`);
        if (!active) return;
        const rows = (Array.isArray(response?.data) ? response.data : []).filter(item => String(item?.BirimKodu) !== '0');
        setRegions(rows);
        const preferred = rows.find(item => String(item.BirimKodu) === String(region?.BirimKodu || storedService?.regionId))
          || (rows.length === 1 ? rows[0] : null);
        if (preferred) {
          setRegion(preferred);
          sessionStorage.setItem(REGION_STORAGE_KEY, JSON.stringify(preferred));
          await loadServices(preferred, storedService);
        }
      } catch {
        if (active) setSelectionError('Bölge listesi alınamadı. Lütfen tekrar deneyin.');
      } finally { if (active) setLoadingRegions(false); }
    };
    loadRegions();
    return () => { active = false; };
  }, []);

  const handleRegionChange = async (newRegion) => {
    setRegion(newRegion);
    selectService(null, newRegion);
    queryClient.clear();
    if (newRegion) {
      sessionStorage.setItem(REGION_STORAGE_KEY, JSON.stringify(newRegion));
      await loadServices(newRegion);
    } else {
      sessionStorage.removeItem(REGION_STORAGE_KEY);
      setServices([]);
    }
  };
  const handleServiceChange = (newService) => { selectService(newService, region); queryClient.clear(); };

  const selectedServiceOption = services.find(item => String(item.BirimKodu) === String(serviceShop?.id)) || null;
  const schedulingKey = useMemo(() => `aron-rotam-${serviceShop?.id || 'no-service'}`, [serviceShop]);
  const selectionControls = (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'minmax(155px, .8fr) minmax(225px, 1.2fr)' },
      gap: 1,
      width: { xs: '100%', sm: 470 },
      flexShrink: 0
    }}>
      <SelectionField label="Bölge" placeholder="Bölge seçin" value={region} options={regions} loading={loadingRegions} icon={<LocationOnOutlinedIcon fontSize="small" />} onChange={handleRegionChange} />
      <SelectionField label="Servis" placeholder={region ? 'Servis adı veya kodu ile arayın' : 'Önce bölge seçin'} value={selectedServiceOption} options={services} loading={loadingServices} disabled={!region || loadingRegions} icon={<SearchIcon fontSize="small" />} onChange={handleServiceChange} />
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6fb' }}>
      {!serviceShop ? (
        <>
          <Paper square elevation={0} sx={{ px: { xs: 1.5, md: 2.5 }, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5, flexDirection: { xs: 'column', md: 'row' }, zIndex: 20 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mr: 'auto' }}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: 'grid', placeItems: 'center', color: '#fff', boxShadow: '0 8px 18px rgba(245,124,0,.25)', background: 'linear-gradient(135deg, #ffad33, #f57c00)' }}><AltRouteIcon /></Box>
              <Box><Typography variant="subtitle1" fontWeight={800}>Aron Rotam</Typography><Typography variant="caption" color="text.secondary">Randevu ve rota planlama merkezi</Typography></Box>
            </Box>
            {selectionControls}
          </Paper>
          {selectionError ? <Alert severity="error" sx={{ m: 2 }}>{selectionError}</Alert> : null}
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 3 }}>
            <Paper variant="outlined" sx={{ maxWidth: 580, width: '100%', p: 3, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
              <ApartmentIcon sx={{ fontSize: 46, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" fontWeight={750} gutterBottom>Çalışılacak servisi seçin</Typography>
              <Typography color="text.secondary">Önce bölgeyi, ardından servisi seçin. Randevu ve rota verileri seçtiğiniz servisin ServiceShopId değeriyle yüklenecektir.</Typography>
            </Paper>
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ToaScheduling key={schedulingKey} selectedServiceShop={serviceShop} headerControls={selectionControls} />
        </Box>
      )}
    </Box>
  );
}

export default function AronRotam() {
  return <QueryClientProvider client={queryClient}><AronRotamContent /></QueryClientProvider>;
}
